import { ArrowsClockwise, EyeSlash, Eye, WarningCircle } from "@phosphor-icons/react";
import { fmtDate } from "../serviceApi";
import { FREQ_LABEL, DAYS } from "./useRecurring";

const Tag = ({ cls, children, testId }) => <span className={`inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid={testId}>{children}</span>;
const ROW = {
  upcoming: "bg-orange-50 border-orange-500 border-t-orange-100 hover:bg-orange-100",
  overdue: "bg-rose-50 border-rose-500 border-t-rose-100 hover:bg-rose-100",
};

// Active recurring schedules pinned above one-time history: overdue (pale red) first, then upcoming (pale orange), soonest due first.
export function RecurringRows({ schedules, hidden, onToggleHidden, onOpen, overdue, selectedId }) {
  const rows = schedules.filter((s) => s.status !== "inactive")
    .sort((a, b) => (a.overdue === b.overdue ? a.next_due.localeCompare(b.next_due) : a.overdue ? -1 : 1));
  if (!rows.length) return null;
  const late = rows.filter((s) => s.overdue).length;
  return (
    <div className="border-b border-orange-200" data-testid="service-recurring-section">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50/60">
        <ArrowsClockwise size={13} weight="bold" className="text-orange-500" />
        <span className="text-[11px] uppercase tracking-widest font-bold text-orange-700 flex-1">
          Recurring ({rows.length}){late > 0 && <span className="text-rose-600" data-testid="service-recurring-overdue-count"> · {late} overdue</span>}
        </span>
        <button type="button" onClick={onToggleHidden} className="text-[11px] font-semibold text-orange-700 hover:underline inline-flex items-center gap-1" data-testid="service-recurring-toggle">
          {hidden ? <><Eye size={12} /> Show</> : <><EyeSlash size={12} /> Hide</>}
        </button>
      </div>
      {!hidden && rows.map((s) => (
        <button key={s.id} type="button" onClick={() => onOpen(s)} data-state={s.overdue ? "overdue" : "upcoming"}
          className={`w-full text-left px-3 py-2 border-l-4 border-t transition-colors ${ROW[s.overdue ? "overdue" : "upcoming"]} ${selectedId === s.id ? "ring-2 ring-inset ring-[#327de3]" : ""}`} data-testid={`service-recurring-row-${s.id}`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            {s.overdue
              ? <Tag cls="bg-rose-600 text-white" testId={`service-recurring-overdue-${s.id}`}><WarningCircle size={11} weight="fill" className="mr-0.5" />Overdue: {fmtDate(s.next_due)}</Tag>
              : <Tag cls="bg-orange-500 text-white">Recurring</Tag>}
            <Tag cls="bg-white text-slate-700 border border-slate-200">{FREQ_LABEL[s.frequency]}</Tag>
            {s.status === "paused" && <Tag cls="bg-slate-200 text-slate-600">Paused</Tag>}
            {s.needs_start_date && <Tag cls="bg-amber-100 text-amber-800" testId={`service-recurring-needs-start-${s.id}`}>Set start date</Tag>}
            {s.warnings.length > 0 && <Tag cls="bg-amber-100 text-amber-800">Check recipients</Tag>}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-semibold text-sm text-slate-800 truncate flex-1">{s.title}</span>
            <span className="text-[11px] text-slate-500 shrink-0">{s.overdue ? "Was due" : "Next due"} <b className={s.overdue ? "text-rose-700" : "text-slate-800"} data-testid={`service-recurring-next-due-${s.id}`}>{fmtDate(s.next_due)}</b></span>
          </div>
          <div className="text-[11px] text-slate-500 truncate">{s.category_name}{s.unit ? ` · ${s.unit}` : ""} · {s.overdue ? (overdue?.enabled === false ? "overdue notices off" : `overdue notice every ${DAYS[overdue?.weekday ?? 1]}`) : `reminds ${s.lead_days} days before`} · {s.channels.join(" + ").toUpperCase()}</div>
        </button>
      ))}
    </div>
  );
}
